import type {
  BadgeEntry,
  FurniItem,
  Gender,
  InventoryItem,
  LeaderboardEntry,
  Look,
  PublicProfile,
  RoomDescriptor,
  RoomQuota,
  RoomSummary,
  RoomTemplateId,
  SelfProfile,
} from '@kopi-kaki/protocol';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

const FRIENDLY: Record<string, string> = {
  username_taken: 'That username is already taken.',
  invalid_credentials: 'Wrong username or password.',
  unauthorized: 'Please log in again.',
  insufficient_funds: "You don't have enough Kaki Dollars for that.",
  guest_restricted: 'Save your account to unlock this. Guests can look around, chat and play.',
  guest_disabled: 'Guest play has ended. Please sign up or log in.',
  guest_limit: 'Too many guest accounts from this network today. Please sign up or log in instead.',
  terms_required: 'Please confirm you are 13 or older and accept the Terms to continue.',
  not_owned: "You haven't unlocked that item yet.",
  muted: 'You are muted right now.',
  banned: 'This account is suspended.',
  forbidden: "You don't have permission to do that.",
  unknown_item: 'That item is not sold here.',
  room_limit: 'You have reached your room limit. Buy another room slot with Gold.',
  slots_maxed: 'You already have every room slot.',
  not_guest: 'This account is already registered.',
  not_found: 'Not found.',
  rate_limited: 'Slow down a little and try again.',
  internal: 'Something went wrong on our side. Please try again.',
};

interface ZodIssue {
  path?: Array<string | number>;
  message?: string;
}

function messageFor(status: number, body: Record<string, unknown>): string {
  const code = typeof body.error === 'string' ? body.error : '';
  if (code === 'bad_request' && Array.isArray(body.issues) && body.issues.length > 0) {
    const issue = body.issues[0] as ZodIssue;
    const field = issue.path?.[0];
    return field ? `${String(field)}: ${issue.message ?? 'invalid'}` : (issue.message ?? 'Invalid input.');
  }
  if (code === 'insufficient_funds' && body.currency === 'gold') {
    return "You don't have enough Gold for that. Top up in the Vault.";
  }
  // A specific code (e.g. guest_limit, sent with 429) beats the generic status text.
  if (FRIENDLY[code]) return FRIENDLY[code]!;
  if (status === 429) return FRIENDLY.rate_limited!;
  return typeof body.message === 'string' ? body.message : `Request failed (${status}).`;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  let response: Response;
  try {
    response = await fetch(path, {
      method,
      credentials: 'same-origin',
      // Fastify rejects an empty body declared as JSON, so only send the
      // header when there is something to send.
      headers: body === undefined ? {} : { 'content-type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, 'network', 'Cannot reach the server. Check your connection.');
  }

  const text = await response.text();
  let data: Record<string, unknown> = {};
  if (text) {
    try {
      data = JSON.parse(text) as Record<string, unknown>;
    } catch {
      data = {};
    }
  }

  if (!response.ok) {
    const code = typeof data.error === 'string' ? data.error : `http_${response.status}`;
    throw new ApiError(response.status, code, messageFor(response.status, data));
  }
  return data as T;
}

export interface AuthResponse {
  user: SelfProfile;
}

export type RoomTab = 'places' | 'active' | 'mine' | 'search';

export type ReportTargetType = 'user' | 'room' | 'chat' | 'design';

const withName = (displayName?: string) => (displayName ? { displayName } : {});

export const api = {
  me: () => request<AuthResponse>('GET', '/api/me'),
  welcomeSeen: () => request<AuthResponse>('POST', '/api/me/welcome-seen'),
  /** Guests and new accounts must confirm they are 13+ and accept the terms. */
  guest: () => request<AuthResponse>('POST', '/api/auth/guest', { acceptTerms: true }),
  login: (username: string, password: string) =>
    request<AuthResponse>('POST', '/api/auth/login', { username, password }),
  register: (username: string, password: string, gender: Gender, displayName?: string) =>
    request<AuthResponse>('POST', '/api/auth/register', {
      username,
      password,
      gender,
      acceptTerms: true,
      ...withName(displayName),
    }),
  upgrade: (username: string, password: string, displayName?: string) =>
    request<AuthResponse>('POST', '/api/auth/upgrade', {
      username,
      password,
      acceptTerms: true,
      ...withName(displayName),
    }),
  logout: () => request<{ ok: true }>('POST', '/api/auth/logout'),
  logoutAll: () => request<{ ok: true }>('POST', '/api/auth/logout-all'),
  changePassword: (currentPassword: string, newPassword: string) =>
    request<{ ok: true }>('POST', '/api/me/password', { currentPassword, newPassword }),
  deleteAccount: (password?: string) =>
    request<{ ok: true }>('DELETE', '/api/me', { confirm: 'DELETE', ...(password ? { password } : {}) }),
  /** URL of the personal-data export; the server sends it as a download. */
  exportUrl: '/api/me/export',
  updateProfile: (patch: { look?: Look; motto?: string }) =>
    request<AuthResponse>('PATCH', '/api/me', patch),

  profile: (id: string) => request<{ profile: PublicProfile }>('GET', `/api/users/${encodeURIComponent(id)}`),

  myBadges: () => request<{ earned: BadgeEntry[]; displayed: string[] }>('GET', '/api/me/badges'),
  setDisplayBadges: (badgeIds: string[]) => request<AuthResponse>('PUT', '/api/me/badges', { badgeIds }),

  rooms: (tab: RoomTab, query = '') => {
    const params = new URLSearchParams({ tab });
    if (tab === 'search') params.set('q', query);
    return request<{ rooms: RoomSummary[] }>('GET', `/api/rooms?${params.toString()}`);
  },
  room: (id: string) =>
    request<{ room: RoomDescriptor; items: FurniItem[]; occupants: number }>(
      'GET',
      `/api/rooms/${encodeURIComponent(id)}`,
    ),
  createRoom: (name: string, template: RoomTemplateId, description?: string) =>
    request<{ room: RoomSummary }>('POST', '/api/rooms', {
      name,
      template,
      ...(description ? { description } : {}),
    }),

  updateRoom: (id: string, patch: { name?: string; description?: string }) =>
    request<{ room: RoomSummary }>('PATCH', `/api/rooms/${encodeURIComponent(id)}`, patch),

  deleteRoom: (id: string) =>
    request<{ ok: true; returnedItems: number }>('DELETE', `/api/rooms/${encodeURIComponent(id)}`),
  roomQuota: () => request<{ quota: RoomQuota }>('GET', '/api/rooms/quota'),
  buyRoomSlot: () => request<{ quota: RoomQuota }>('POST', '/api/rooms/slots'),

  buy: (kind: string) => request<{ money: number; item: InventoryItem }>('POST', '/api/shop/buy', { kind }),
  inventory: () => request<{ items: InventoryItem[] }>('GET', '/api/me/inventory'),
  leaderboard: () =>
    request<{ respects: LeaderboardEntry[] }>('GET', '/api/leaderboard'),

  report: (report: {
    targetType: ReportTargetType;
    targetId: string;
    reason: 'spam' | 'harassment' | 'inappropriate' | 'cheating' | 'other';
    details?: string;
  }) => request<{ ok: true }>('POST', '/api/reports', report),
};

export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return 'Something went wrong.';
}
